<template>
  <div :class="containerClass" class="page-section">
    <div class="d-flex align-items-center mb-1">
      <h2 class="mb-0 mr-2">CareReady Wallet</h2>
      <i
        class="material-icons icon-16pt clickable-item"
        v-b-popover.hover.top="`CareReady Wallet is your ready-to-work profile.`"
        >info</i
      >
    </div>
    <p class="text-muted mb-3">
      CareReady Wallet is your ready-to-work profile for CNA and CMA. Upload your key documents once so you can apply
      faster, start sooner, and stay compliant.
    </p>

    <div class="card mb-3">
      <div class="card-header">
        <h5 class="my-0">Upload Guidelines</h5>
      </div>
      <div style="font-size: 16px" class="px-3 my-4">
        Follow the guidelines mentioned below while uploading your documents. If you have any query, contact support for
        assistance.
        <br />
        <br />
        <b>What are the acceptable file formats and file sizes for uploads?</b>
        <ul>
          <li>Acceptable file formats: PDF, PNG, JPG</li>
        </ul>
        <b> What are the page image guidelines? </b>
        <ul>
          <li>Choose a well-lit area</li>
          <li>Lay the document flat and have nothing in the background</li>
          <li>Ensure clear legibility and name visibility</li>
          <li>Frame the entire document, no cut corners</li>
          <li>Focus and stabilize the camera</li>
        </ul>
      </div>

      <div class="row" :style="isMobileScreen ? '' : 'margin-left: 140px; margin-right: 140px'">
        <div class="col-md-6 text-center mb-2">
          <img src="/images/docs/good-example.png" alt="" />
          <div class="my-2">
            <md-icon class="text-success" style="font-size: 19px">check_circle</md-icon>
            <b class="text-success"> Good ID </b>
          </div>
        </div>
        <div class="col-md-6 text-center mb-2">
          <img src="/images/docs/bad-example1.png" alt="" />
          <div class="my-2">
            <md-icon class="text-danger" style="font-size: 19px">block</md-icon>
            <b class="text-danger"> Bad: </b>
            The ID is blurry
          </div>
        </div>
      </div>
      <div
        v-if="!isShowMore"
        class="text-center text-primary clickable-item mb-3"
        @click.prevent="() => (isShowMore = true)"
      >
        show more
      </div>
      <div v-if="isShowMore">
        <div class="row" :style="isMobileScreen ? '' : 'margin-left: 140px; margin-right: 140px'">
          <div class="col-md-6 text-center mb-2" v-for="(doc, index) in badDocImages" :key="index">
            <img :src="doc.img" alt="" />
            <div class="my-2">
              <md-icon class="text-danger" style="font-size: 19px">block</md-icon>
              <b class="text-danger"> Bad: </b>
              {{ doc.text }}
            </div>
          </div>
        </div>
        <div class="text-center text-primary clickable-item mb-3" @click.prevent="() => (isShowMore = false)">
          show less
        </div>
      </div>
    </div>

    <div class="card mb-3">
      <div class="card-header d-flex align-items-center justify-content-between flex-wrap">
        <div>
          <h5 class="my-0">Role &amp; License Preference</h5>
          <small class="text-muted">Pick your active track to tailor credential details.</small>
        </div>
      </div>
      <div class="card-body">
        <b-form-group label="Role" label-for="selectedRoleTop" label-class="form-label mb-1">
          <b-form-select
            id="selectedRoleTop"
            v-model="selectedRole"
            :options="[
              { value: 'cna', text: 'CNA' },
              { value: 'cma', text: 'CMA' },
            ]"
          />
        </b-form-group>
      </div>
    </div>

    <div v-if="rejectedPiis.length" class="alert alert-warning mb-3" role="alert">
      <div class="d-flex align-items-start">
        <md-icon class="text-warning mr-2" style="font-size: 18px">report_problem</md-icon>
        <div style="min-width: 0">
          <div class="font-weight-bold">You have {{ rejectedPiis.length }} rejected document(s)</div>
          <div class="small text-muted">Click the "Rejected" tag next to a document to view the reason.</div>
        </div>
      </div>
    </div>

    <b-skeleton-wrapper :loading="isPIITypeConfigLoading || isPIIConfigLoading || isLoading">
      <template #loading>
        <div class="card">
          <div class="card-body">
            <div class="d-flex mb-1">
              <div class="flex">
                <b-skeleton width="100%"></b-skeleton>
                <b-skeleton width="55%"></b-skeleton>
                <b-skeleton width="70%"></b-skeleton>
              </div>
            </div>
          </div>
        </div>
      </template>
      <div class="card" v-if="isObjectEmpty">
        <div class="card-body">
          <div class="d-flex mb-1">
            <div class="flex">
              <i>No Enrolled program found to upload documents.</i>
            </div>
          </div>
        </div>
      </div>

      <b-form
        class="col-md-12 px-0 pt-0"
        @submit.prevent="onSubmit"
        v-else-if="!isObjectEmpty && hasValueOtherThanDisabled"
      >
        <div class="card mb-3" id="piiCard" style="scroll-margin-top: 68px">
          <div class="card-header d-flex align-items-center justify-content-between flex-wrap">
            <div>
              <h5 class="my-0">Identity &amp; Work Eligibility</h5>
              <small class="text-muted"
                >Upload the documents below (only upload Passport/Green Card if they apply to you).</small
              >
            </div>
          </div>
          <div class="card-body">
            <div v-if="isPIITypeConfigLoading || isPIIConfigLoading">
              <b-skeleton width="85%"></b-skeleton>
              <b-skeleton width="55%"></b-skeleton>
              <b-skeleton width="70%"></b-skeleton>
            </div>
            <div v-else>
              <div v-for="doc in identityDocs" :key="doc.id" class="mb-2">
                <b-form-group
                  :label-for="get(doc, 'type')"
                  label-class="form-label"
                  class="row-align-items-center mb-0"
                  :id="`pii-field-${get(doc, 'type')}`"
                >
                  <template #label>
                    <span>
                      {{ get(doc, 'title')
                      }}{{ get(piiTypesConfig, get(doc, 'type')) === PII_CONFIG_TYPE.REQUIRED ? '*' : '' }}
                    </span>
                    <b-badge
                      v-if="isPiiRejected(get(doc, 'type'))"
                      variant="danger"
                      class="ml-2 clickable-item"
                      @click="openRejectionReason(get(doc, 'type'))"
                    >
                      Rejected
                    </b-badge>
                  </template>
                  <span v-if="!get(piiTypes, get(doc, 'type'))">
                    <a
                      href="#"
                      class="text-primary"
                      @click.prevent="
                        openAddModal(doc, get(piiTypesConfig, get(doc, 'type')) === PII_CONFIG_TYPE.REQUIRED)
                      "
                      ><i class="fas fa-upload mr-2"></i>Upload File
                    </a>
                  </span>
                  <span v-else>
                    <div class="row">
                      <div class="col-md-10">
                        <a
                          href="#"
                          @click.prevent="
                            downloadFile({ fileUrl: get(piiTypes, get(doc, 'type')), removeTimestamp: true })
                          "
                          v-b-popover.hover.top="'Download'"
                          ><i class="fas fa-file text-primary"></i>
                          {{ parseFileNameFromUrl(get(piiTypes, get(doc, 'type')), true) }}
                        </a>
                        <span
                          v-if="get(expiresAt, get(doc, 'type'))"
                          :class="{ 'text-danger': isPIIExpired(get(expiresAt, get(doc, 'type'))) }"
                        >
                          ({{ isPIIExpired(get(expiresAt, get(doc, 'type'))) ? 'Expired' : 'Expires' }} on:
                          {{ formatDateSimple(get(expiresAt, get(doc, 'type'))) }})
                        </span>
                      </div>
                      <div class="text-right col-md-2">
                        <a
                          href="#"
                          class="text-danger"
                          @click.prevent="removePiiFile(get(doc, 'type'), get(doc, 'type'))"
                          ><i class="fas fa-times-circle"></i> Remove
                        </a>
                      </div>
                    </div>
                  </span>

                  <b-form-invalid-feedback
                    :state="
                      !get($v.piiTypes, get(doc, 'type')).required && get($v.piiTypes, get(doc, 'type')).$dirty
                        ? false
                        : null
                    "
                    >This field is required.</b-form-invalid-feedback
                  >
                </b-form-group>
              </div>
            </div>
          </div>
        </div>

        <div class="card mb-3" style="scroll-margin-top: 68px">
          <div class="card-header d-flex align-items-center justify-content-between flex-wrap">
            <div>
              <h5 class="my-0">{{ credentialHeading }}</h5>
              <small class="text-muted">Provide your credential information and supporting documents.</small>
            </div>
          </div>
          <div class="card-body">
            <div class="alert alert-info py-2" role="alert">
              Upload at least one of the following: Basic Life Support (BLS) Certification or CPR License. One is
              required.
            </div>
            <div class="row">
              <div class="col-md-6">
                <b-form-group :label="licenseLabel" label-for="licenseNumber" label-class="form-label">
                  <b-form-input id="licenseNumber" v-model="licenseNumber" :placeholder="licensePlaceholder" />
                </b-form-group>
              </div>
            </div>

            <div v-if="isPIITypeConfigLoading || isPIIConfigLoading">
              <b-skeleton width="85%"></b-skeleton>
              <b-skeleton width="55%"></b-skeleton>
              <b-skeleton width="70%"></b-skeleton>
            </div>
            <div v-else>
              <div v-for="doc in credentialDocs" :key="doc.id" class="mb-2">
                <b-form-group
                  :label-for="get(doc, 'type')"
                  label-class="form-label"
                  class="row-align-items-center mb-0"
                  :id="`pii-field-${get(doc, 'type')}`"
                >
                  <template #label>
                    <span>
                      {{ get(doc, 'title')
                      }}{{ get(piiTypesConfig, get(doc, 'type')) === PII_CONFIG_TYPE.REQUIRED ? '*' : '' }}
                    </span>
                    <b-badge
                      v-if="isPiiRejected(get(doc, 'type'))"
                      variant="danger"
                      class="ml-2 clickable-item"
                      @click="openRejectionReason(get(doc, 'type'))"
                    >
                      Rejected
                    </b-badge>
                  </template>
                  <span v-if="!get(piiTypes, get(doc, 'type'))">
                    <a
                      href="#"
                      class="text-primary"
                      @click.prevent="
                        openAddModal(doc, get(piiTypesConfig, get(doc, 'type')) === PII_CONFIG_TYPE.REQUIRED)
                      "
                      ><i class="fas fa-upload mr-2"></i>Upload File
                    </a>
                  </span>
                  <span v-else>
                    <div class="row">
                      <div class="col-md-10">
                        <a
                          href="#"
                          @click.prevent="
                            downloadFile({ fileUrl: get(piiTypes, get(doc, 'type')), removeTimestamp: true })
                          "
                          v-b-popover.hover.top="'Download'"
                          ><i class="fas fa-file text-primary"></i>
                          {{ parseFileNameFromUrl(get(piiTypes, get(doc, 'type')), true) }}
                        </a>
                        <span
                          v-if="get(expiresAt, get(doc, 'type'))"
                          :class="{ 'text-danger': isPIIExpired(get(expiresAt, get(doc, 'type'))) }"
                        >
                          ({{ isPIIExpired(get(expiresAt, get(doc, 'type'))) ? 'Expired' : 'Expires' }} on:
                          {{ formatDateSimple(get(expiresAt, get(doc, 'type'))) }})
                        </span>
                      </div>
                      <div class="text-right col-md-2">
                        <a
                          href="#"
                          class="text-danger"
                          @click.prevent="removePiiFile(get(doc, 'type'), get(doc, 'type'))"
                          ><i class="fas fa-times-circle"></i> Remove
                        </a>
                      </div>
                    </div>
                  </span>

                  <b-form-invalid-feedback
                    :state="
                      !get($v.piiTypes, get(doc, 'type')).required && get($v.piiTypes, get(doc, 'type')).$dirty
                        ? false
                        : null
                    "
                    >This field is required.</b-form-invalid-feedback
                  >
                </b-form-group>
              </div>
            </div>
          </div>
        </div>

        <div class="card mb-3" style="scroll-margin-top: 68px">
          <div class="card-header d-flex align-items-center justify-content-between flex-wrap">
            <div>
              <h5 class="my-0">Health &amp; Compliance</h5>
              <small class="text-muted">Keep your health records up to date.</small>
            </div>
          </div>
          <div class="card-body">
            <div v-if="isPIITypeConfigLoading || isPIIConfigLoading">
              <b-skeleton width="85%"></b-skeleton>
              <b-skeleton width="55%"></b-skeleton>
              <b-skeleton width="70%"></b-skeleton>
            </div>
            <div v-else>
              <div v-for="doc in healthDocs" :key="doc.id" class="mb-2">
                <b-form-group
                  :label-for="get(doc, 'type')"
                  label-class="form-label"
                  class="row-align-items-center mb-0"
                  :id="`pii-field-${get(doc, 'type')}`"
                >
                  <template #label>
                    <span>
                      {{ get(doc, 'title')
                      }}{{ get(piiTypesConfig, get(doc, 'type')) === PII_CONFIG_TYPE.REQUIRED ? '*' : '' }}
                    </span>
                    <b-badge
                      v-if="isPiiRejected(get(doc, 'type'))"
                      variant="danger"
                      class="ml-2 clickable-item"
                      @click="openRejectionReason(get(doc, 'type'))"
                    >
                      Rejected
                    </b-badge>
                  </template>
                  <span v-if="!get(piiTypes, get(doc, 'type'))">
                    <a
                      href="#"
                      class="text-primary"
                      @click.prevent="
                        openAddModal(doc, get(piiTypesConfig, get(doc, 'type')) === PII_CONFIG_TYPE.REQUIRED)
                      "
                      ><i class="fas fa-upload mr-2"></i>Upload File
                    </a>
                  </span>
                  <span v-else>
                    <div class="row">
                      <div class="col-md-10">
                        <a
                          href="#"
                          @click.prevent="
                            downloadFile({ fileUrl: get(piiTypes, get(doc, 'type')), removeTimestamp: true })
                          "
                          v-b-popover.hover.top="'Download'"
                          ><i class="fas fa-file text-primary"></i>
                          {{ parseFileNameFromUrl(get(piiTypes, get(doc, 'type')), true) }}
                        </a>
                        <span
                          v-if="get(expiresAt, get(doc, 'type'))"
                          :class="{ 'text-danger': isPIIExpired(get(expiresAt, get(doc, 'type'))) }"
                        >
                          ({{ isPIIExpired(get(expiresAt, get(doc, 'type'))) ? 'Expired' : 'Expires' }} on:
                          {{ formatDateSimple(get(expiresAt, get(doc, 'type'))) }})
                        </span>
                      </div>
                      <div class="text-right col-md-2">
                        <a
                          href="#"
                          class="text-danger"
                          @click.prevent="removePiiFile(get(doc, 'type'), get(doc, 'type'))"
                          ><i class="fas fa-times-circle"></i> Remove
                        </a>
                      </div>
                    </div>
                  </span>

                  <b-form-invalid-feedback
                    :state="
                      !get($v.piiTypes, get(doc, 'type')).required && get($v.piiTypes, get(doc, 'type')).$dirty
                        ? false
                        : null
                    "
                    >This field is required.</b-form-invalid-feedback
                  >
                </b-form-group>
              </div>
            </div>
          </div>
        </div>

        <div class="card mb-3" style="scroll-margin-top: 68px">
          <div class="card-header">
            <h5 class="my-0">Review &amp; Submit</h5>
          </div>
          <div class="card-body">
            <b-form-checkbox id="confirmOwnership" v-model="confirmChecked" class="mb-3">
              I confirm these documents are mine and accurate.
            </b-form-checkbox>
            <b-button :disabled="isFormLoading || !confirmChecked" variant="primary" type="submit" class="btn-normal">
              <i v-if="isFormLoading" class="fas fa-circle-notch fa-spin mr-2"></i>
              Submit CareReady Wallet
            </b-button>
          </div>
        </div>
      </b-form>
    </b-skeleton-wrapper>
    <add-pii-modal
      :show-modal="showDocModal"
      :title="addDocTitle"
      :type="addDocType"
      :isRequired="isRequired"
      :isDocDateReq="isDocDateReq"
      :questions="questions"
      @add="piiDocAdded"
      @close="hideModal"
    />

    <b-modal ref="rejectionReasonModal" centered title="Rejection reason" hide-footer>
      <div class="mb-2">
        <div class="font-weight-bold">{{ selectedRejectionTitle }}</div>
        <div class="text-muted small" v-if="selectedRejectionAt">
          Rejected at: {{ formatDateSimple(selectedRejectionAt) }}
        </div>
      </div>
      <div class="alert alert-danger mb-0" role="alert">
        {{ selectedRejectionReason || 'No reason provided' }}
      </div>
    </b-modal>
  </div>
</template>

<script>
import { forEach, get, keyBy } from 'lodash';
import { mapActions } from 'vuex';
import AddPiiModal from '../../SuperAdminPortal/Students/AddPiiModal.vue';
import Page from '../../../components/Page.vue';
import { PII_TYPES, PII_CONFIG_TYPE } from '../../../common/constants';

import {
  parseFileNameFromUrl,
  formatToAPIDate,
  formatDateSimple,
  isDateExpired as isPIIExpired,
} from '../../../common/utils';
import { requiredIf } from 'vuelidate/lib/validators';

export default {
  components: { AddPiiModal },
  extends: Page,

  data() {
    return {
      title: 'CareReady Wallet',
      PII_TYPES,
      PII_CONFIG_TYPE,
      showDocModal: false,
      addDocType: '',
      addDocTitle: '',
      hasValueOtherThanDisabled: false,
      isPIITypeConfigLoading: false,
      piis: {
        state_id: { id: 'state_id', type: 'state_id', title: 'State ID', custom_questions: [], req_expiry_date: false },
        social_sec_card: {
          id: 'social_sec_card',
          type: 'social_sec_card',
          title: 'Social Security Card',
          custom_questions: [],
          req_expiry_date: false,
        },
        passport: { id: 'passport', type: 'passport', title: 'Passport', custom_questions: [], req_expiry_date: false },
        green_card: {
          id: 'green_card',
          type: 'green_card',
          title: 'Green Card',
          custom_questions: [],
          req_expiry_date: false,
        },
        completion_cert: {
          id: 'completion_cert',
          type: 'completion_cert',
          title: 'Certification of Completion',
          custom_questions: [],
          req_expiry_date: false,
        },
        cpr: { id: 'cpr', type: 'cpr', title: 'CPR License', custom_questions: [], req_expiry_date: false },
        bls: {
          id: 'bls',
          type: 'bls',
          title: 'Basic Life Support (BLS) Certification',
          custom_questions: [],
          req_expiry_date: false,
        },
        first_aid: {
          id: 'first_aid',
          type: 'first_aid',
          title: 'First Aid Certification (optional)',
          custom_questions: [],
          req_expiry_date: false,
        },
        tb_test: { id: 'tb_test', type: 'tb_test', title: 'TB Test', custom_questions: [], req_expiry_date: false },
        immunization: {
          id: 'immunization',
          type: 'immunization',
          title: 'Immunization',
          custom_questions: [],
          req_expiry_date: false,
        },
      },
      piiTypes: {
        state_id: '',
        social_sec_card: '',
        passport: '',
        green_card: '',
        completion_cert: '',
        cpr: '',
        bls: '',
        first_aid: '',
        tb_test: '',
        immunization: '',
      },
      expiresAt: {
        state_id: null,
        social_sec_card: null,
        passport: null,
        green_card: null,
        completion_cert: null,
        cpr: null,
        bls: null,
        first_aid: null,
        tb_test: null,
        immunization: null,
      },
      isLoading: false,
      isPIIConfigLoading: false,
      isObjectEmpty: false,
      student_piis: [],

      piiTypesConfig: {},
      windowWidth: window.innerWidth,
      isRequired: false,
      isDocDateReq: false,
      questions: [],
      isShowMore: false,
      badDocImages: [
        {
          img: '/images/docs/bad-example2.png',
          text: 'The name is cropped',
        },
        {
          img: '/images/docs/bad-example3.png',
          text: 'The photo is cropped',
        },
        {
          img: '/images/docs/bad-example4.png',
          text: 'Zoomed in too much',
        },
        {
          img: '/images/docs/bad-example5.png',
          text: 'Photocopy of an ID',
        },
      ],
      student_piis_by_type: {},
      rejectedPiis: [],
      selectedRejectionType: null,
      selectedRejectionTitle: '',
      selectedRejectionReason: '',
      selectedRejectionAt: null,
      selectedRole: 'cna',
      licenseNumber: '',
      confirmChecked: false,
      isInitialLoad: true,
    };
  },

  validations() {
    return {
      piiTypes: {
        state_id: { required: requiredIf(() => get(this.piiTypesConfig, 'state_id') === PII_CONFIG_TYPE.REQUIRED) },
        social_sec_card: {
          required: requiredIf(() => get(this.piiTypesConfig, 'social_sec_card') === PII_CONFIG_TYPE.REQUIRED),
        },
        passport: { required: requiredIf(() => get(this.piiTypesConfig, 'passport') === PII_CONFIG_TYPE.REQUIRED) },
        green_card: { required: requiredIf(() => get(this.piiTypesConfig, 'green_card') === PII_CONFIG_TYPE.REQUIRED) },
        completion_cert: {
          required: requiredIf(() => get(this.piiTypesConfig, 'completion_cert') === PII_CONFIG_TYPE.REQUIRED),
        },
        cpr: { required: requiredIf(() => get(this.piiTypesConfig, 'cpr') === PII_CONFIG_TYPE.REQUIRED) },
        bls: { required: requiredIf(() => get(this.piiTypesConfig, 'bls') === PII_CONFIG_TYPE.REQUIRED) },
        first_aid: { required: requiredIf(() => get(this.piiTypesConfig, 'first_aid') === PII_CONFIG_TYPE.REQUIRED) },
        tb_test: { required: requiredIf(() => get(this.piiTypesConfig, 'tb_test') === PII_CONFIG_TYPE.REQUIRED) },
        immunization: {
          required: requiredIf(() => get(this.piiTypesConfig, 'immunization') === PII_CONFIG_TYPE.REQUIRED),
        },
      },
    };
  },

  computed: {
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Required Documents', active: true },
      ];
    },
    isFormLoading() {
      return this.isLoading || this.isPIIConfigLoading;
    },
    isMobileScreen() {
      return this.windowWidth <= 1200;
    },
    licenseLabel() {
      return this.selectedRole === 'cma' ? 'CMA Certificate / License Number' : 'CNA Certificate / License Number';
    },
    licensePlaceholder() {
      return this.selectedRole === 'cma' ? 'Enter CMA license number' : 'Enter CNA license number';
    },
    credentialHeading() {
      return this.selectedRole === 'cma' ? 'CMA Credential Details' : 'CNA Credential Details';
    },
    identityDocs() {
      const keys = ['state_id', 'social_sec_card', 'passport', 'green_card'];
      return keys.map(key => this.piis[key]).filter(Boolean);
    },
    credentialDocs() {
      const keys = ['completion_cert', 'bls', 'cpr', 'first_aid'];
      return keys.map(key => this.piis[key]).filter(Boolean);
    },
    healthDocs() {
      const keys = ['tb_test', 'immunization'];
      return keys.map(key => this.piis[key]).filter(Boolean);
    },
  },

  watch: {
    selectedRole(newVal, oldVal) {
      if (newVal !== oldVal && !this.isInitialLoad) {
        this.licenseNumber = '';
      }
    },
  },

  methods: {
    ...mapActions('student', ['getMyStudentProfile', 'updateMyPIIDocs']),
    ...mapActions('fileDownload', ['downloadFile']),
    parseFileNameFromUrl,
    formatDateSimple,
    get,
    isPIIExpired,

    handleResize() {
      this.windowWidth = window.innerWidth;
    },

    openAddModal(doc, isRequired) {
      if (this.isFormLoading) return;

      this.showDocModal = true;
      this.addDocType = doc.type;
      this.addDocTitle = doc.title;
      this.questions = doc.custom_questions;
      this.isDocDateReq = doc.req_expiry_date;
      this.isRequired = isRequired;
    },
    piiDocAdded(file, url, type, date, questions) {
      this.showDocModal = false;
      this.documentAdded = true;
      this.piiTypes[type] = url;
      this.expiresAt[type] = date;
      this.addDocType = '';
      this.addDocTitle = '';
      this.questions = questions;
    },
    hideModal() {
      this.showDocModal = false;
      this.addDocType = '';
      this.addDocTitle = '';
    },

    async removePiiFile(fileType) {
      const isConfirmed = await this.$bvModal.msgBoxConfirm(
        'Please confirm that you want to remove the selected document. You will need to reupload the document if required.',
        {
          title: 'Are you sure?',
          size: 'md',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Yes',
          cancelTitle: 'No',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true,
        },
      );
      if (isConfirmed) {
        this.piiTypes[fileType] = null;
        this.expiresAt[fileType] = null;
      }
    },

    async onSubmit(skipConfirm = false) {
      this.isLoading = true;

      try {
        const missingRequired = [];

        if (!this.piiTypes.state_id) missingRequired.push('State ID');
        if (!this.piiTypes.social_sec_card) missingRequired.push('Social Security Card');
        if (!this.licenseNumber) missingRequired.push('Certificate / License Number');
        if (!this.piiTypes.tb_test) missingRequired.push('TB Test');
        if (!this.piiTypes.immunization) missingRequired.push('Immunization');
        if (!this.piiTypes.bls && !this.piiTypes.cpr) {
          missingRequired.push('Basic Life Support (BLS) Certification or CPR License');
        }

        if (missingRequired.length) {
          this.makeToast({
            variant: 'danger',
            msg: `Please upload or enter: ${missingRequired.join(', ')}.`,
          });
          this.isLoading = false;
          return;
        }

        if (!skipConfirm && !this.confirmChecked) {
          this.makeToast({ variant: 'danger', msg: 'Please confirm your documents before submitting.' });
          this.isLoading = false;
          return;
        }

        const renderedTypes = Object.keys(this.piiTypes);
        const specialTypes = ['care_ready_role', 'cert_license_no'];

        let piiArr = this.student_piis
          .filter(pii => !renderedTypes.includes(pii.type) && !specialTypes.includes(pii.type))
          .map(pii => ({
            questions_answers: pii.questions_answers || [],
            type: pii.type,
            document_url: pii.document_url,
            expire_at: pii.expire_at,
          }));

        for (const item in this.piiTypes) {
          if (this.piiTypes[item]) {
            piiArr.push({
              questions_answers: this.questions,
              type: item,
              document_url: this.piiTypes[item],
              expire_at: this.expiresAt[item] ? formatToAPIDate(this.expiresAt[item]) : null,
            });
          }
        }

        if (this.selectedRole) {
          piiArr.push({
            questions_answers: [],
            type: 'care_ready_role',
            document_url: this.selectedRole,
            expire_at: null,
          });
        }

        piiArr.push({
          questions_answers: [],
          type: 'cert_license_no',
          document_url: this.licenseNumber,
          expire_at: null,
        });

        await this.updateMyPIIDocs({
          data: { student_piis: piiArr },
        });
        this.makeToast({ variant: 'success', msg: 'Documents Updated Successfully' });
        await this.fetchStudentProfile();
        if (!skipConfirm) {
          this.confirmChecked = false;
        }
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }

      this.isLoading = false;
    },

    isPiiRejected(type) {
      return !!get(this.student_piis_by_type, `${type}.is_rejected`, false);
    },

    openRejectionReason(type) {
      const pii = get(this.student_piis_by_type, type);
      if (!pii) return;
      this.selectedRejectionType = type;
      this.selectedRejectionTitle = this.getPiiTitleByType(type);
      this.selectedRejectionReason = pii.rejection_reason;
      this.selectedRejectionAt = pii.rejected_at;
      this.$refs.rejectionReasonModal.show();
    },

    getPiiTitleByType(type) {
      return get(this.piis, `${type}.title`) || type;
    },

    async downloadGivenFile({ fileUrl, removeTimestamp = true }) {
      const h = this.$createElement;

      this.makeToast({
        variant: 'primary',
        msg: h('span', [h('i', { class: 'fas fa-download mr-2' }), 'Downloading File']),
        toaster: 'b-toaster-bottom-left',
      });

      await this.downloadFile({ fileUrl, removeTimestamp });

      this.makeToast({
        variant: 'success',
        msg: h('span', [h('i', { class: 'fas fa-download mr-2' }), 'File Downloaded']),
        toaster: 'b-toaster-bottom-left',
      });
    },

    async fetchStudentProfile() {
      this.isLoading = true;

      const resp = await this.getMyStudentProfile(true);

      this.student_piis_by_type = keyBy(resp.student_piis || [], 'type');
      this.rejectedPiis = (resp.student_piis || []).filter(pii => pii.is_rejected);

      this.student_piis = resp.student_piis;

      this.student_piis
        .filter(pii => pii.type != 'transcript')
        .forEach(pii => {
          if (Object.prototype.hasOwnProperty.call(this.piiTypes, pii.type)) {
            this.piiTypes[pii.type] = pii.document_url;
            this.expiresAt[pii.type] = pii.expire_at;
          }
        });
      const rolePii = (this.student_piis || []).find(pii => pii.type === 'care_ready_role');
      const inputPii = (this.student_piis || []).find(pii => pii.type === 'cert_license_no');

      this.selectedRole = rolePii ? rolePii.document_url : 'cna';
      this.licenseNumber = inputPii ? inputPii.document_url : '';
      this.$nextTick(() => {
        this.isInitialLoad = false;
      });
      forEach(this.piiTypes, (type, key) => {
        if (!type) {
          this.piiTypes[key] = null;
        }
      });

      this.isLoading = false;
    },

    async fetchStdConfigPiis() {
      this.isPIITypeConfigLoading = true;
      try {
        const types = Object.keys(this.piis || {});
        const optionalTypes = ['passport', 'green_card', 'first_aid', 'completion_cert', 'bls', 'cpr'];
        this.piiTypesConfig = types.reduce((acc, type) => {
          acc[type] = optionalTypes.includes(type) ? PII_CONFIG_TYPE.OPTIONAL : PII_CONFIG_TYPE.REQUIRED;
          return acc;
        }, {});
        this.isObjectEmpty = types.length === 0;
        this.hasValueOtherThanDisabled = types.length > 0;
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }

      this.isPIITypeConfigLoading = false;
    },
  },

  async mounted() {
    window.addEventListener('resize', this.handleResize);

    await this.fetchStdConfigPiis();
    await this.fetchStudentProfile();
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>
